/**
 * Where the stores register is kept, and how a change to it is kept.
 *
 * Two places, and the screens above do not need to know which:
 *
 *   - **the platform's stores register** (routers/stores.py), when the screen
 *     is connected to a platform that holds one for this site. A change is
 *     sent as the steps the operation reported (storesOps.ts), all or none,
 *     and the platform — which may have seen stock move since this screen last
 *     read it — can still refuse. Its refusal is shown in its own words.
 *   - **a site note** under `estate.stores`, as it always was: on the sample,
 *     in the demonstration, and on a platform that holds no stores register
 *     for the site yet. Nothing about that behaviour has changed.
 *
 * A platform that holds no register but has the note says so, and the desk
 * offers to bring the note across once (`importNote`). A platform that cannot
 * answer at all — an older one without the stores API, or a person without
 * `stores:read` — is treated as holding none, so the desk still works from the
 * note rather than going blank.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSession } from "../../app/SessionContext";
import type { StoresRegister } from "../../domain/stores";
import type { CounterResult } from "../../domain/storesEdit";
import type { StoresOp } from "../../domain/storesOps";
import { mockStores } from "../../mock/stores";
import { latestNote } from "../shared/noteStore";
import { useEntered } from "../executive/store";
import { t } from "../../i18n";

export const STORES_TOPIC = "estate.stores";

/** A change that has been worked out and is ready to keep. */
export type Kept = { ok: true; register: StoresRegister; note?: string; ops?: StoresOp[] } | CounterResult;

export interface StoresHold {
  value: StoresRegister;
  /** "platform" when the platform holds this site's register; "note" otherwise */
  mode: "platform" | "note";
  loading: boolean;
  savedTo: string | null;
  /** keep a change: returns the platform's refusal, or null when it was kept */
  commit: (result: Kept) => Promise<string | null>;
  /** a register is kept as a note that the platform has not taken in yet */
  noteWaiting: boolean;
  /** bring the note across, once: returns what was said, and whether it was a refusal */
  importNote: () => Promise<{ ok: boolean; text: string }>;
}

/**
 * What the register is before anyone has entered one (build 7).
 *
 * Under the demo, the sample estate. On the live platform, a site with no
 * register yet starts with the store locations and nothing in them: the
 * sample's stock, orders and requests used to stand in, drawn as if entered by
 * hand, and every stock figure on the desk and the Logistics board was invented.
 */
export function startingStores(platform: boolean): StoresRegister {
  const m = mockStores();
  if (!platform) return m;
  return { currency: m.currency, locations: m.locations, items: [], movements: [], reservations: [], requisitions: [], orders: [] };
}

function platformKey(siteId: string) {
  return ["stores-platform", siteId] as const;
}

/** The platform's register for this site, when connected to one; never asked on the sample. */
export function usePlatformStores() {
  const session = useSession();
  const { source } = session;
  const siteId = session.site.id;
  return useQuery({
    queryKey: platformKey(siteId),
    enabled: source.kind === "platform",
    staleTime: 30_000,
    retry: false,
    queryFn: () => source.storesRegister(siteId),
  });
}

/**
 * The register for screens that only read it — the work order's parts, the
 * executive board's feed. `settled` is false until it is known which of the
 * two places holds it, so a reader does not draw the note and then the
 * platform's register over it.
 */
export function useStoresRead(): { register: StoresRegister | undefined; settled: boolean } {
  const session = useSession();
  const { source } = session;
  const siteId = session.site.id;
  const platform = usePlatformStores();
  /* the same note the desk reads, and the same key, so the two cannot disagree */
  const note = useQuery({
    queryKey: ["exec-entered", siteId, STORES_TOPIC],
    staleTime: 60_000,
    queryFn: () => latestNote<StoresRegister>(source, siteId, STORES_TOPIC),
  });
  if (source.kind === "platform" && platform.isPending) return { register: undefined, settled: false };
  if (platform.data?.held) return { register: platform.data.register, settled: true };
  return { register: note.data?.value, settled: !note.isPending };
}

export function useStores(): StoresHold {
  const session = useSession();
  const qc = useQueryClient();
  const { source } = session;
  const siteId = session.site.id;
  const who = session.principal.name;
  const entered = useEntered<StoresRegister>(STORES_TOPIC, startingStores(source.kind === "platform"));
  const platform = usePlatformStores();
  const onPlatform = platform.data?.held === true;
  /*
   * What was just kept, shown until the platform's own copy comes back — the
   * screen answers at once, and the platform's version replaces it the moment
   * it arrives, ids and all.
   */
  const [kept, setKept] = useState<{ register: StoresRegister; after: number } | null>(null);
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const fresh = kept && kept.after === platform.dataUpdatedAt ? kept.register : null;

  const reread = () => qc.invalidateQueries({ queryKey: platformKey(siteId) });

  return {
    value: onPlatform ? (fresh ?? platform.data!.register) : entered.value,
    mode: onPlatform ? "platform" : "note",
    loading: source.kind === "platform" ? platform.isPending || entered.loading : entered.loading,
    savedTo: onPlatform ? savedTo : entered.savedTo,
    noteWaiting: !onPlatform && platform.data?.noteWaiting === true,
    commit: async (result) => {
      if (!onPlatform) {
        await entered.save(result.register);
        return null;
      }
      const ops = result.ops ?? [];
      if (!ops.length) return t("This change could not be sent to the platform, so it was not kept.");
      try {
        await source.storesApply(siteId, ops);
      } catch (e) {
        /* read again: whatever made the platform refuse is in its copy, not in this one */
        void reread();
        return e instanceof Error ? e.message : String(e);
      }
      setKept({ register: result.register, after: platform.dataUpdatedAt });
      setSavedTo(t("saved on the platform under {who}", { who }));
      void reread();
      return null;
    },
    importNote: async () => {
      try {
        const r = await source.storesImportNote(siteId);
        await reread();
        return {
          ok: true,
          text: t("Brought onto the platform: {items} lines and {movements} movements. The platform keeps the register from now on.", {
            items: r.items,
            movements: r.movements,
          }),
        };
      } catch (e) {
        return { ok: false, text: e instanceof Error ? e.message : String(e) };
      }
    },
  };
}
